<template>
    <Modal
        id="create-withdrawal-modal"
        v-model:open="open"
        title="modal_title_create_withdrawal"
        @cancel="cancel"
        @submit="submit"
    >
        <div class="flex h-auto w-full flex-col gap-2">
            <SelectField
                v-model="selectedWallet"
                :append-to-body="true"
                :options="walletOptions"
                label="wallet"
                name="wallet"
            />
            <SelectField
                v-if="selectedWallet"
                v-model="selectedBankAccount"
                :append-to-body="true"
                :options="bankAccountOptions ?? []"
                label="bank_account"
                name="bank_account"
            />
            <NumberField
                v-if="selectedWallet && selectedBankAccount"
                v-model="amount"
                label="withdrawal_amount"
                name="withdrawal_amount"
                :required="true"
            />
            <TextField
                v-if="selectedWallet && selectedBankAccount && amount"
                v-model="comment"
                label="comment"
                name="comment"
                :required="true"
            />
            <p
                v-if="errorMessage"
                class="text-xs text-red-500"
            >
                {{ errorMessage }}
            </p>
        </div>
        <template #actions>
            <button
                :class="{
                    'btn bg-primary-accent-500 text-white': !disableSubmit,
                    'btn bg-gray-500 text-white': disableSubmit,
                }"
                :disabled="disableSubmit"
            >
                {{ $t('create') }}
            </button>
        </template>
    </Modal>
</template>
<script setup lang="ts">
import { Modal } from '@src/components'
import NumberField from '@src/components/NumberField.vue'
import SelectField from '@src/components/SelectField.vue'
import TextField from '@src/components/TextField.vue'
import { BankAccount } from '@src/types'
import { WalletData } from '@src/utils/api/wallets'
import { useVModels } from '@vueuse/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    open: boolean
    wallets: WalletData[]
}>()

const emit = defineEmits(['cancel', 'submit', 'update:open'])

const { open } = useVModels(props, emit)

const i18n = useI18n()

const comment = ref<string | null>(null)
const selectedWallet = ref<string | null>(null)
const amount = ref<number | null>(null)
const walletOptions = computed(() => {
    return props.wallets?.map((wallet) => ({
        label:
            wallet?.external_id +
            ' - ' +
            wallet?.retrievable_balance?.amount +
            ' ' +
            wallet?.retrievable_balance?.currency,
        value: wallet?.id,
    }))
})
const selectedBankAccount = ref<string | null>(null)
const bankAccountOptions = computed(() => {
    return props.wallets
        .find((wallet) => wallet.id === selectedWallet.value)
        ?.bank_accounts?.map((bankAccount: BankAccount) => ({
            label: bankAccount.iban,
            value: bankAccount.id,
        }))
})
const disableSubmit = computed<boolean>(() => {
    return (
        !selectedWallet.value ||
        !selectedBankAccount.value ||
        !amount.value ||
        !!errorMessage.value
    )
})
const errorMessage = ref('')

function cancel() {
    emit('cancel')
}

function submit() {
    emit('submit', {
        amount: amount.value,
        bank_account_id: selectedBankAccount.value,
        comment: comment.value,
    })
}

watch(selectedWallet, (walletId) => {
    if (!walletId) {
        errorMessage.value = ''
        return
    }

    const selection = props.wallets.find((wallet) => wallet.id === walletId)
    if (selection) {
        if (selection?.retrievable_balance?.amount === 0) {
            errorMessage.value = i18n.t(
                'error_withdrawal_amount_must_be_greater_than_0'
            )
        } else if (!selection.can_withdraw) {
            errorMessage.value = i18n.t('error_withdrawal_not_allowed')
        } else if (selection.bank_accounts.length < 1) {
            errorMessage.value = i18n.t('error_bank_account_required')
        } else {
            errorMessage.value = ''
        }
    }
})

watch(
    () => amount.value,
    (selectedAmount) => {
        const wallet = props.wallets.find(
            (wallet) => wallet.id === selectedWallet.value
        )
        if (
            wallet?.retrievable_balance.amount &&
            selectedAmount &&
            selectedAmount > wallet.retrievable_balance.amount
        ) {
            errorMessage.value = i18n.t(
                'error_withdrawal_amount_is_greater_than_balance'
            )
        } else if (
            wallet?.withdrawal_limit.amount &&
            selectedAmount &&
            selectedAmount > wallet.withdrawal_limit.amount
        ) {
            errorMessage.value = i18n.t(
                'error_withdrawal_amount_is_greater_than_limit'
            )
        } else if (
            (selectedAmount &&
                selectedAmount > 0 &&
                selectedAmount <= (wallet?.retrievable_balance.amount ?? 0)) ??
            0
        ) {
            errorMessage.value = ''
        }
    }
)

onMounted(() => {
    if (props.wallets.length === 1) {
        selectedWallet.value = props.wallets[0].id ?? null
    }
})
</script>
